import cn from 'classnames'

import { SMB_GUIDED_WIZARD_TEXT as TEXT } from '../../../configs/guidedWizardTranslations'
import { TPlanningSmbGuidedWizard } from '../../../hooks/usePlanningSmbGuidedWizard'
import { IPlanningSmbGuidedWizardProps } from '../../../model/guidedWizardTypes'
import { TSmbSplitResult } from '../../../model/types'
import styles from '../PlanningSmbGuidedWizard.module.scss'

import { PlanningSmbGuidedGrowth } from './PlanningSmbGuidedControls'
import { PlanningSmbGuidedText as Text, TGuidedTranslationParams } from './PlanningSmbGuidedText'

export const formatGuidedPercent = (value: number) => String(Math.round(value * 10) / 10)

const SplitBar = ({ value, baseline }: { value: TSmbSplitResult; baseline: boolean }) => (
  <div className={cn(styles.track, { [styles.baselineTrack]: baseline })}>
    {value.isEmpty ? (
      <span className={styles.emptyTrack}>—</span>
    ) : (
      <>
        <span className={styles.smbSegment} style={{ width: `${value.smb}%` }}>
          <span>
            <Text text={TEXT.smb} /> {formatGuidedPercent(value.smb)}%
          </span>
        </span>
        <span className={styles.enterpriseSegment}>
          <span>
            <Text text={TEXT.enterprise} /> {formatGuidedPercent(value.eu)}%
          </span>
        </span>
      </>
    )}
  </div>
)

export const PlanningSmbGuidedOverview = ({
  props,
  editor,
  params,
}: {
  props: IPlanningSmbGuidedWizardProps
  editor: TPlanningSmbGuidedWizard
  params: TGuidedTranslationParams
}) => {
  const last = editor.getValue(props.tree, editor.years[editor.years.length - 1])
  const isShortHorizon = editor.years.length < 3
  const baseline = props.offeringBaseline
  const rows = [
    { year: props.fYear - 1, baseline: true, value: baseline },
    ...editor.years.map((year) => ({
      year,
      baseline: false,
      value: editor.getValue(props.tree, year),
    })),
  ]
  return (
    <div className={styles.overview}>
      <div className={styles.share}>
        <h2 className={styles.sectionTitle}>
          <Text text={isShortHorizon ? TEXT.shareTitlePeriod : TEXT.shareTitle} params={params} />
        </h2>
        <p className={styles.description}>
          <Text text={TEXT.shareNote} params={params} />
        </p>
        {baseline.isEmpty && (
          <p className={styles.noBaseline}>
            <Text text={TEXT.noBaseline} params={params} />
          </p>
        )}
        <div className={styles.legend}>
          <span>
            <i className={styles.smbLegend} aria-hidden />
            <Text text={TEXT.smb} />
          </span>
          <span>
            <i className={styles.enterpriseLegend} aria-hidden />
            <Text text={TEXT.enterpriseLegend} />
          </span>
        </div>
        <div className={styles.bars}>
          {rows.map((row) => (
            <div className={styles.barRow} key={row.year}>
              <span className={styles.year}>
                <Text text={TEXT.fiscalYear} params={{ year: String(row.year).slice(-2) }} />
                <small>
                  <Text text={row.baseline ? TEXT.actual : TEXT.plan} />
                </small>
              </span>
              <SplitBar value={row.value} baseline={row.baseline} />
            </div>
          ))}
        </div>
      </div>
      <aside className={styles.growthPanel}>
        <span className={styles.controlTitle}>
          <Text text={TEXT.growth} />
        </span>
        <p className={styles.growthHint}>
          <Text text={TEXT.growthHint} params={params} />
        </p>
        <PlanningSmbGuidedGrowth editor={editor} />
        <p className={styles.growthResult}>
          <Text
            text={
              baseline.isEmpty
                ? isShortHorizon
                  ? TEXT.growthResultNoBaselinePeriod
                  : TEXT.growthResultNoBaseline
                : isShortHorizon
                  ? TEXT.growthResultPeriod
                  : TEXT.growthResult
            }
            params={{
              ...params,
              baseline: baseline.isEmpty ? '—' : formatGuidedPercent(baseline.smb),
              planned: last.isEmpty ? '—' : formatGuidedPercent(last.smb),
            }}
          />
        </p>
        <div className={styles.enough}>
          <span aria-hidden>✓ </span>
          <Text text={TEXT.enough} params={{ ...params, count: editor.categories.length }} />
        </div>
      </aside>
    </div>
  )
}
